/*
  List.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    fontWeight: 400,
    lineHeight: '1.5',
    margin: 0,
    padding: theme.space(1, 0),
    listStyle: 'none',
    '& > li': {
      display: 'flex',
      fontSize: '1rem',
      padding: theme.space(1, 2),
      cursor: 'pointer',
      outline: 'none',
      userSelect: 'none',
      textAlign: 'left',
      textDecoration: 'none',
      transition: 'background-color 0.1s ease',
      '&:hover,&:focus-visible': {
        backgroundColor: theme.action_hover,
      },
      '& > .iconContainer': {
        paddingRight: theme.space(2),
      },
    },
    '&.dense': {
      padding: theme.space(0.5, 0),
      '& > li': {
        fontSize: '0.875rem',
        padding: theme.space(0.5, 1),
      }
    },
    '&.disabled > li': {
      cursor: 'inherit',
      '&:hover,&:focus-visible': {
        backgroundColor: 'transparent',
      },
    }
  },
}));

/**
 * Menu when something is right-clicked on the graph.
 */
export default function List({
  
  // List props.
  options,
  disabled = false,
  dense = false,
  getOptionLabel = (opt) => typeof opt === 'string' ? opt : (opt?.label || ''),  // Guesses the label.
  getOptionValue = (opt) => typeof opt === 'string' ? opt : (opt?.value || ''),  // Guesses the value.
  getOptionIcon,
  renderOption,
  onClickOption,

  // Other props.
  className,
  ...rest

}) {

  // Get className.
  const classes = useStyles();
  let rootClass = 'List';
  if (disabled) {
    rootClass += ' disabled';
  }
  if (dense) {
    rootClass += ' dense';
  }
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  // TODO: Allow change of items using arrow keys.

  return (
    <ul className={cls} {...rest}>
      {
        Boolean(options) && options.map(opt => {

          const useLabel = getOptionLabel(opt);
          const useValue = getOptionValue(opt);

          // Option item event handlers.
          const handleClick = disabled ? undefined : (event) => {
            event.preventDefault();
            if (onClickOption) {
              onClickOption(event, useValue, opt);
            }
          };
          const handleKeyDown = disabled ? undefined : (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              if (onClickOption) {
                onClickOption(event, useValue, opt);
              }
            }
          };
          const handleKeyUp = disabled ? undefined : (event) => {
            if (event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              if (onClickOption) {
                onClickOption(event, useValue, opt);
              }
            }
          };

          const icon = typeof getOptionIcon === 'function' ? getOptionIcon(opt) : null;

          return (
            <li
              key={useValue}
              title={useLabel}
              tabIndex={disabled ? -1 : 0}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
            >
              {
                icon != null &&
                <span className="iconContainer">{ icon }</span>
              }
              { useLabel }
            </li>
          );
        })
      }
    </ul>
  );
}
